import { useState } from "react";
import { IForm } from "../../";
import VehicleChildForm from "./components/vehicleChildForm";
import { requiredFieldsByStep } from "./utils/required-fields-by-step";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { fieldsLabels } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../../enums/occurrenceStep";

const ThirdDiagnosisForm = (props: IForm) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [changeStepErrorMessage, setChangeStepErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = (props.occurrence?.occurrenceType === "P"
    ? requiredFieldsByStep.THIRD_DIAGNOSIS_PREVENTIVE_CHECKUP
    : requiredFieldsByStep.THIRD_DIAGNOSIS
  ).filter(f => props.occurrence?.occurrenceType !== 'D' || !['vehicleStopped', 'hasLink', 'mainOccurrence', 'tasNumber', 'tasStatus'].includes(f));

  const isDealership = props.occurrence?.occurrenceType === 'D';
  const serviceHiddenFields = isDealership
    ? ['estimatedTime','partOrderStatus','osNumber','osDtOpenAt','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','solutionProposed','criticality','createdBy','dtStart','dtEnd','modules','estimateTimeRepair','vehicleStopped','hasLink','mainOccurrence','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload']
    : ['timeOpenProtocol','country','driverCheckInDriver','mechanicLocation','towTruckLocation','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload','tasNumber','tasStatus','checklist','solutionProposed'];

  return (
    <div>
      <div className="text-size-h4 font-bold">3ª Diagnose</div>
      <div className="text-color-status-resting text-size-14">
        Atribua os dados para relacionar a etapa de terceira diagnose
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "source",
          "modules",
          "hasLink",
          "mainOccurrence",
          "serviceBaySchedule",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end gap-3">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
                setHasError(!isValid);
                
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              Salvar
            </button>
            
            <FinalizeOccurrenceButton
              occurrence={props.occurrence}
              finishOccurrence={props.finishOccurrence}
              disabledAllFields={props.disabledAllFields}
              persona={props.persona}
              stepId={props.step?.id}
            />
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao salvar"
        body="Ocorreu um erro ao salvar a etapa de terceiro diagnóstico. Por favor, tente novamente ou entre em contato com o suporte."
      />

      <ErrorDialog
        show={!!changeStepErrorMessage}
        onClose={() => setChangeStepErrorMessage(null)}
        title="Erro ao alterar etapa"
        body={changeStepErrorMessage ?? ""}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Terceiro diagnóstico salvo com sucesso"
        body={
          <div className="space-y-4">
            <div>O terceiro diagnóstico foi salvo com sucesso.</div>
            {!hasError && (
              <div>
                Deseja avançar para a etapa{" "}
                {OccurrenceStepLabelEnum.FOURTH_DIAGNOSIS}?{" "}
              </div>
            )}
            {hasError && (
              <>
                <div>
                  É necessário preencher todos os campos obrigatórios para
                  avançar/retroceder de etapa.
                </div>
                <div>Campos obrigatórios não preenchidos:</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {fieldsLabels[field as keyof typeof fieldsLabels]}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
        footer={
          <button
            className="btn btn-primary"
            disabled={hasError || !props.changeStep || !props.occurrence}
            onClick={async () => {
              if (!props.summaryStep?.id || !props.occurrence) return;
              try {
                await props.changeStep!(
                  props.occurrence!,
                  props.summaryStep.id,
                  OccurrenceStepEnum.FOURTH_DIAGNOSIS
                );
                setShowSuccessDialog(false);
                props.onClose();
              } catch (error: any) {
                setShowSuccessDialog(false);
                setChangeStepErrorMessage(error?.message ?? "Erro ao alterar etapa. Verifique os dados e tente novamente.");
              }
            }}
          >
            Avançar etapa
          </button>
        }
      />
    </div>
  );
};

export default ThirdDiagnosisForm;
